//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeIframeSrc } from '../../../utils/securityUtils';
import { BookX } from 'lucide-react';

interface WikiTabProps {
  controlBasePath: string;
  teamspaceName: string;
  loopName: string;
  hasWiki?: boolean;
}

const WikiTab: React.FC<WikiTabProps> = ({
  controlBasePath,
  teamspaceName,
  loopName,
  hasWiki,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Intercept clicks on cross-origin links inside the wiki iframe and open
  // them in a new browser tab instead of letting the iframe navigate/frame
  // them (which the parent CSP would block). Mirrors SwitcherIframe behaviour.
  const handleExternalLinks = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      const anchorElement = target as HTMLAnchorElement;
      if (anchorElement.target === '_blank') return;
      const anchorHref = anchorElement.href;
      if (anchorHref) {
        try {
          const newUrlObj = new URL(anchorHref);
          const isDifferentOrigin = newUrlObj.origin !== window.location.origin;
          if (isDifferentOrigin) {
            if (
              newUrlObj.protocol !== 'https:' &&
              newUrlObj.protocol !== 'http:'
            ) {
              return;
            }
            event.preventDefault();
            window.open(
              `${newUrlObj.origin}${newUrlObj.pathname}${newUrlObj.search}${newUrlObj.hash}`,
              '_blank',
              'noopener,noreferrer',
            );
          }
        } catch {
          // Malformed URL — do not navigate
        }
      }
    }
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const attachListeners = () => {
      try {
        iframe.contentWindow?.addEventListener('click', handleExternalLinks);
      } catch {
        // Cross-origin access may throw — safe to ignore
      }
    };

    iframe.addEventListener('load', attachListeners);
    return () => {
      iframe.removeEventListener('load', attachListeners);
      try {
        iframe.contentWindow?.removeEventListener('click', handleExternalLinks);
      } catch {
        // Cross-origin teardown — safe to ignore
      }
    };
  }, [handleExternalLinks]);

  // Derive allowed origins for sanitizeIframeSrc — required for cross-origin Control deployments.
  const allowedOrigins = useMemo(() => {
    if (!controlBasePath) return [];
    try {
      const url = new URL(controlBasePath);
      return url.origin !== window.location.origin ? [url.origin] : [];
    } catch {
      return [];
    }
  }, [controlBasePath]);

  if (!controlBasePath) {
    return (
      <div data-testid="wiki-tab" className="mt-6">
        <div
          role="alert"
          data-testid="wiki-tab-error"
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {t('wiki.errorNoControl')}
        </div>
      </div>
    );
  }

  if (!loopName) {
    return (
      <div data-testid="wiki-tab" className="mt-6">
        <div
          role="alert"
          data-testid="wiki-tab-error"
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {t('wiki.errorNoLoop')}
        </div>
      </div>
    );
  }

  if (hasWiki === false) {
    return (
      <div
        data-testid="wiki-tab"
        className="mt-6 flex flex-col items-center justify-center gap-4"
        style={{ height: '70vh' }}
      >
        <div
          data-testid="wiki-tab-unavailable"
          className="flex max-w-md flex-col items-center text-center"
        >
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-muted">
            <BookX className="size-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('wiki.unavailableHeading')}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t('wiki.unavailableDescription')}
          </p>
        </div>
      </div>
    );
  }

  const rawSrc = `${controlBasePath}/${teamspaceName}/${loopName}/wiki?embed=true`;
  const safeSrc = sanitizeIframeSrc(rawSrc, allowedOrigins);

  return (
    <div
      data-testid="wiki-tab"
      className="relative mt-6"
      style={{ height: '70vh' }}
    >
      {isLoading && (
        <div
          data-testid="wiki-tab-loading"
          aria-label={t('wiki.loading')}
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="text-sm text-muted-foreground">
            {t('wiki.loading')}
          </span>
        </div>
      )}
      <iframe
        ref={iframeRef}
        data-testid="wiki-tab-iframe"
        title={t('wiki.iframeTitle')}
        src={safeSrc}
        sandbox="allow-scripts allow-same-origin allow-downloads allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
        style={{ width: '100%', height: '100%', border: 'none' }}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

export default WikiTab;
